import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Container, Card, Row, Col, Form, Button, Badge, Table, Spinner, Modal, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FaCalendarDays, FaUserCheck, FaClock, FaTrashCan, FaPlus, FaMagnifyingGlass, FaArrowLeft, FaArrowRight } from 'react-icons/fa6';
import { api } from '../../services/api.js';
import Menu from '../../comum/Menu/menu.jsx';
import Footer from '../../comum/Footer/index.jsx';
import Foto from "../Components/Foto/index.jsx";
import './cadastroescala.css';

const fotoCache = new Map();

// Componente para busca e exibição de Foto de Perfil em Background
const AvatarFuncionario = React.memo(({ chapa, nome, info01, info02, onSelectFoto, tamanho = 42 }) => {
    const [fotoSrc, setFotoSrc] = useState(null);

    useEffect(() => {
        if (!chapa) return;
        let isMounted = true;
        const hoje = new Date().toISOString().slice(0, 10);
        const chave = `func_${chapa}_${hoje}`;

        if (fotoCache.has(chave)) {
            setFotoSrc(fotoCache.get(chave));
            return;
        }

        api.get(`/usuario/foto/${chapa}`)
            .then(res => {
                if (isMounted && res?.data?.foto) {
                    fotoCache.set(chave, res.data.foto);
                    setFotoSrc(res.data.foto);
                }
            })
            .catch(() => { });

        return () => { isMounted = false; };
    }, [chapa]);

    const handleClick = (e) => {
        if (e) e.stopPropagation();
        if (fotoSrc && onSelectFoto) {
            onSelectFoto({
                foto: `data:image/jpeg;base64,${fotoSrc}`,
                nome: nome || "",
                info01: info01 || `Matrícula: ${chapa}`,
                info02: info02 || ""
            });
        }
    };

    return (
        <div
            className="avatar-escala-box"
            onClick={handleClick}
            style={{ width: tamanho, height: tamanho, cursor: fotoSrc ? 'pointer' : 'default' }}
        >
            {fotoSrc ? (
                <img
                    src={`data:image/jpeg;base64,${fotoSrc}`}
                    alt="Perfil"
                    style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover', border: '2px solid #8b0000' }}
                />
            ) : (
                <div className="avatar-placeholder">👤</div>
            )}
        </div>
    );
});

function CadastroEscala() {
    const navigate = useNavigate();
    const hoje = new Date();
    const painelCalendarioRef = useRef(null);
    
    // Estados do Período
    const [mesAno, setMesAno] = useState(() => {
        const m = String(hoje.getMonth() + 1).padStart(2, '0');
        return `${hoje.getFullYear()}-${m}`;
    });

    // Filtros e Lojas Permitidas
    const [lojasPermitidas, setLojasPermitidas] = useState([]);
    const [lojaSelecionada, setLojaSelecionada] = useState('');
    const [buscaNome, setBuscaNome] = useState('');

    // Dados Globais
    const [funcionarios, setFuncionarios] = useState([]);
    const [horarios, setHorarios] = useState([]);
    const [funcSelecionado, setFuncSelecionado] = useState(null);

    // Estados de Carga
    const [loadingFunc, setLoadingFunc] = useState(false);
    const [loadingEscala, setLoadingEscala] = useState(false);
    const [salvando, setSalvando] = useState(false);

    // Estado da Escala Atual do Funcionário
    const [escalaFunc, setEscalaFunc] = useState([]);
    const [horarioPadrao, setHorarioPadrao] = useState('');
    const [horarioSubst, setHorarioSubst] = useState('');
    const [modoMarcacao, setModoMarcacao] = useState('F'); // 'F' = Folga, 'S' = Substituto

    // Modais e Imagens
    const [fotoAmpliada, setFotoAmpliada] = useState(null);

    // 1. Carregar Lojas do usuário e catálogo de horários
    useEffect(() => {
        const lojas = JSON.parse(localStorage.getItem("lojasPermitidas") || "[]");
        const minhaLoja = localStorage.getItem("loja") || (lojas[0] || '1');
        setLojasPermitidas(lojas);
        setLojaSelecionada(String(minhaLoja));

        api.get('/ponto/horarios')
            .then(res => setHorarios(res.data || []))
            .catch(() => console.error("Erro ao carregar horários."));
    }, []);

    // 2. Carregar Lista de Colaboradores da Loja
    const carregarFuncionarios = useCallback(() => {
        if (!lojaSelecionada) return;
        setLoadingFunc(true);
        api.get(`/ponto/${lojaSelecionada}/funcionarios`)
            .then(res => {
                const lista = res.data || [];
                setFuncionarios(lista);
                if (lista.length > 0) {
                    setFuncSelecionado(lista[0]);
                } else {
                    setFuncSelecionado(null);
                    setEscalaFunc([]);
                }
            })
            .catch(err => {
                console.error(err);
                setFuncionarios([]);
            })
            .finally(() => setLoadingFunc(false));
    }, [lojaSelecionada]);

    useEffect(() => {
        carregarFuncionarios();
    }, [carregarFuncionarios]);

    // 3. Carregar Escala do Colaborador Selecionado
    const carregarEscalaFuncionario = useCallback(() => {
        if (!funcSelecionado) return;
        setLoadingEscala(true);
        const [ano, mes] = mesAno.split('-');
        const mesanoParam = `${mes}${ano}`;

        api.get(`/ponto/${funcSelecionado.chapa}/escala?mes=${mesanoParam}`)
            .then(res => {
                const dados = res.data || [];
                setEscalaFunc(dados);

                const itemFolga = dados.find(d => d.tipo === 'F');
                if (itemFolga) {
                    setHorarioPadrao(itemFolga.horario || itemFolga.codigo || '');
                }
            })
            .catch(() => setEscalaFunc([]))
            .finally(() => setLoadingEscala(false));
    }, [funcSelecionado, mesAno]);

    useEffect(() => {
        carregarEscalaFuncionario();
    }, [carregarEscalaFuncionario]);

    // Seleção de funcionário com rolagem automática para o painel em mobile
    const handleSelecionarFuncionario = (func) => {
        setFuncSelecionado(func);
        if (window.innerWidth <= 991 && painelCalendarioRef.current) {
            setTimeout(() => {
                painelCalendarioRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        }
    };

    // Filtragem de funcionários por texto
    const funcionariosFiltrados = useMemo(() => {
        return funcionarios.filter(f => 
            f.nome?.toLowerCase().includes(buscaNome.toLowerCase()) ||
            f.chapa?.includes(buscaNome) ||
            f.funcao?.toLowerCase().includes(buscaNome.toLowerCase())
        );
    }, [funcionarios, buscaNome]);

    // Lógica para montar a Grade do Calendário
    const diasMesGrid = useMemo(() => {
        const [anoStr, mesStr] = mesAno.split('-');
        const ano = parseInt(anoStr, 10);
        const mes = parseInt(mesStr, 10) - 1;

        const primeiroDia = new Date(ano, mes, 1);
        const ultimoDia = new Date(ano, mes + 1, 0);
        const dias = [];

        for (let i = 0; i < primeiroDia.getDay(); i++) {
            dias.push({ vazio: true });
        }

        for (let d = 1; d <= ultimoDia.getDate(); d++) {
            const diaFmt = String(d).padStart(2, '0');
            const mesFmt = String(mes + 1).padStart(2, '0');
            const dataIso = `${ano}-${mesFmt}-${diaFmt}`;
            const dataFormatadaStr = `${diaFmt}/${mesFmt}/${ano}`;

            const info = escalaFunc.find(e => {
                const eData = e.data?.split('T')[0];
                return eData === dataIso || e.data === dataFormatadaStr;
            });

            dias.push({
                dia: d,
                dataIso,
                dataFormatadaStr,
                info
            });
        }

        while (dias.length % 7 !== 0) {
            dias.push({ vazio: true });
        }

        return dias;
    }, [mesAno, escalaFunc]);

    // Alternar / Marcar um Dia no Calendário
    const handleCellClick = (itemDia) => {
        if (itemDia.vazio || !funcSelecionado) return;

        const { dataFormatadaStr, dataIso, info } = itemDia;

        if (modoMarcacao === 'F') {
            if (!horarioPadrao) {
                Swal.fire('Atenção', 'Selecione o Horário de Trabalho Padrão antes de definir as folgas.', 'warning');
                return;
            }

            if (info && info.tipo === 'F') {
                setEscalaFunc(prev => prev.filter(e => !e.data.includes(dataIso) && !e.data.includes(dataFormatadaStr)));
            } else {
                setEscalaFunc(prev => [
                    ...prev.filter(e => !e.data.includes(dataIso) && !e.data.includes(dataFormatadaStr)),
                    { data: dataIso, horario: horarioPadrao, tipo: 'F', codigo: horarioPadrao }
                ]);
            }
        } else if (modoMarcacao === 'S') {
            if (!horarioSubst) {
                Swal.fire('Atenção', 'Selecione o Horário Substituto para aplicar neste dia.', 'warning');
                return;
            }

            setEscalaFunc(prev => [
                ...prev.filter(e => !e.data.includes(dataIso) && !e.data.includes(dataFormatadaStr)),
                { data: dataIso, horario: horarioSubst, tipo: 'S', codigo: horarioSubst }
            ]);
        }
    };

    // Gravação da Escala em Lote
    const handleSalvarEscala = async () => {
        if (!funcSelecionado) return;
        setSalvando(true);

        try {
            const [ano, mes] = mesAno.split('-');
            const mesanoParam = `${mes}${ano}`;

            await api.delete(`/ponto/escala/limpar?chapa=${funcSelecionado.chapa}&data=${mesanoParam}`);

            for (const item of escalaFunc) {
                let dataFormat = item.data;

                if (dataFormat.includes('T')) {
                    dataFormat = dataFormat.split('T')[0];
                }

                if (dataFormat.includes('-')) {
                    const [pAno, pMes, pDia] = dataFormat.split('-');
                    dataFormat = `${pDia.padStart(2, '0')}/${pMes.padStart(2, '0')}/${pAno}`;
                }

                await api.post('/ponto/escala/gravar', {
                    chapa: funcSelecionado.chapa,
                    data: dataFormat,
                    horario: item.horario || item.codigo,
                    tipo: item.tipo
                });
            }

            Swal.fire('Sucesso!', 'Escala gravada com sucesso.', 'success');
            carregarEscalaFuncionario();
        } catch (err) {
            console.error(err);
            Swal.fire('Erro', 'Ocorreu uma falha ao gravar a escala.', 'error');
        } finally {
            setSalvando(false);
        }
    };

    // Exclusão completa da Escala do mês
    const handleLimparEscala = async () => {
        if (!funcSelecionado) return;

        const res = await Swal.fire({
            title: 'Confirmar Exclusão',
            text: `Deseja apagar toda a escala de ${funcSelecionado.nome} no mês selecionado?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#8b0000',
            confirmButtonText: 'Sim, Excluir'
        });

        if (!res.isConfirmed) return;

        setSalvando(true);
        try {
            const [ano, mes] = mesAno.split('-');
            const mesanoParam = `${mes}${ano}`;
            await api.delete(`/ponto/escala/limpar?chapa=${funcSelecionado.chapa}&data=${mesanoParam}`);
            setEscalaFunc([]);
            Swal.fire('Excluído!', 'A escala do colaborador foi limpa.', 'success');
        } catch (err) {
            Swal.fire('Erro', 'Não foi possível excluir a escala.', 'error');
        } finally {
            setSalvando(false);
        }
    };

    // Navegação de Colaboradores
    const handleNavegarFunc = (direcao) => {
        if (!funcSelecionado || funcionariosFiltrados.length === 0) return;
        const idx = funcionariosFiltrados.findIndex(f => f.chapa === funcSelecionado.chapa);
        if (direcao === 'prev' && idx > 0) {
            handleSelecionarFuncionario(funcionariosFiltrados[idx - 1]);
        } else if (direcao === 'next' && idx < funcionariosFiltrados.length - 1) {
            handleSelecionarFuncionario(funcionariosFiltrados[idx + 1]);
        }
    };

    return (
        <div className="bg-light min-vh-100 d-flex flex-column" style={{ paddingTop: '80px' }}>
            <Menu modulo="ARBOR" />

            <Container fluid className="px-2 px-md-4 py-3 flex-grow-1" style={{ maxWidth: '1400px' }}>
                {/* Cabeçalho */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-3">
                    <div>
                        <h4 className="fw-bold m-0 text-dark d-flex align-items-center fs-5 fs-md-4">
                            <FaCalendarDays className="me-2 text-danger" />
                            Gestão de Escala de Funcionários
                        </h4>
                        <small className="text-muted">Cadastre e altere folgas e horários da equipe da loja</small>
                    </div>

                    <div className="d-flex align-items-center gap-2 bg-white p-2 rounded shadow-sm border flex-wrap">
                        <Form.Label className="small fw-bold m-0">Loja:</Form.Label>
                        <Form.Select
                            size="sm"
                            className="input-mobile-nozoom"
                            style={{ width: '110px' }}
                            value={lojaSelecionada}
                            onChange={e => setLojaSelecionada(e.target.value)}
                        >
                            {lojasPermitidas.map(l => (
                                <option key={l} value={l}>Loja {String(l).padStart(2, '0')}</option>
                            ))}
                        </Form.Select>

                        <Form.Label className="small fw-bold m-0 ms-1">Mês/Ano:</Form.Label>
                        <Form.Control
                            type="month"
                            size="sm"
                            className="input-mobile-nozoom"
                            style={{ width: '145px' }}
                            value={mesAno}
                            onChange={e => setMesAno(e.target.value)}
                        />
                    </div>
                </div>

                <Row className="g-3">
                    {/* COLUNA DA ESQUERDA: LISTA DE FUNCIONÁRIOS DA LOJA */}
                    <Col xs={12} lg={4} xl={3}>
                        <Card className="border-0 shadow-sm h-100">
                            <Card.Header className="bg-white fw-bold py-2.5">
                                <InputGroup size="sm">
                                    <InputGroup.Text><FaMagnifyingGlass /></InputGroup.Text>
                                    <Form.Control
                                        placeholder="Buscar colaborador..."
                                        className="input-mobile-nozoom"
                                        value={buscaNome}
                                        onChange={e => setBuscaNome(e.target.value)}
                                    />
                                </InputGroup>
                            </Card.Header>
                            <Card.Body className="p-0 overflow-auto" style={{ maxHeight: '420px' }}>
                                {loadingFunc ? (
                                    <div className="text-center py-4">
                                        <Spinner animation="border" size="sm" className="text-danger" />
                                    </div>
                                ) : (
                                    <div className="list-group list-group-flush">
                                        {funcionariosFiltrados.map((func) => {
                                            const isSelected = funcSelecionado?.chapa === func.chapa;
                                            return (
                                                <button
                                                    key={func.chapa}
                                                    type="button"
                                                    className={`list-group-item list-group-item-action p-2 border-0 d-flex align-items-center gap-2 ${isSelected ? 'bg-danger-subtle fw-bold border-start border-4 border-danger' : ''}`}
                                                    onClick={() => handleSelecionarFuncionario(func)}
                                                >
                                                    <AvatarFuncionario
                                                        chapa={func.chapa}
                                                        nome={func.nome}
                                                        info01={`Matrícula: ${func.chapa}`}
                                                        info02={`Loja ${lojaSelecionada}`}
                                                        onSelectFoto={setFotoAmpliada}
                                                    />
                                                    <div className="text-start overflow-hidden w-100">
                                                        <div className="text-truncate text-uppercase small">{func.nome}</div>
                                                        <small className="text-muted d-block" style={{ fontSize: '0.72rem' }}>
                                                            Mat: {func.chapa} | {func.funcao || 'Colaborador'}
                                                        </small>
                                                    </div>
                                                </button>
                                            );
                                        })}

                                        {funcionariosFiltrados.length === 0 && (
                                            <div className="text-center py-4 text-muted small">
                                                Nenhum funcionário encontrado.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* COLUNA DA DIREITA: CALENDÁRIO E CONFIGURAÇÃO DA ESCALA */}
                    <Col xs={12} lg={8} xl={9} ref={painelCalendarioRef}>
                        {funcSelecionado ? (
                            <Card className="border-0 shadow-sm">
                                <Card.Header className="bg-white p-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
                                    <div className="d-flex align-items-center gap-2.5">
                                        <AvatarFuncionario
                                            chapa={funcSelecionado.chapa}
                                            nome={funcSelecionado.nome}
                                            info01={`Matrícula: ${funcSelecionado.chapa}`}
                                            info02={`Loja ${lojaSelecionada}`}
                                            onSelectFoto={setFotoAmpliada}
                                            tamanho={46}
                                        />
                                        <div>
                                            <h6 className="fw-bold m-0 text-uppercase text-dark small">{funcSelecionado.nome}</h6>
                                            <small className="text-muted d-block" style={{ fontSize: '0.75rem' }}>
                                                Matrícula: <strong>{funcSelecionado.chapa}</strong> | Função: {funcSelecionado.funcao}
                                            </small>
                                        </div>
                                    </div>

                                    <div className="d-flex align-items-center gap-1">
                                        <Button size="sm" variant="outline-secondary" onClick={() => handleNavegarFunc('prev')}>
                                            <FaArrowLeft />
                                        </Button>
                                        <Button size="sm" variant="outline-secondary" onClick={() => handleNavegarFunc('next')}>
                                            <FaArrowRight />
                                        </Button>
                                    </div>
                                </Card.Header>

                                <Card.Body className="p-2 p-md-3">
                                    {/* CONTROLES E SELEÇÃO DE HORÁRIOS */}
                                    <Row className="g-2 mb-3 bg-light p-2.5 rounded border">
                                        <Col xs={12} md={6}>
                                            <Form.Label className="small fw-bold mb-1">Horário Principal (Escala Padrão):</Form.Label>
                                            <Form.Select
                                                size="sm"
                                                className="input-mobile-nozoom"
                                                value={horarioPadrao}
                                                onChange={e => setHorarioPadrao(e.target.value)}
                                            >
                                                <option value="">Selecione o Horário...</option>
                                                {horarios.map(h => (
                                                    <option key={h.codigo} value={h.codigo}>
                                                        {h.codigo} - {h.descricao}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Col>

                                        <Col xs={12} md={6}>
                                            <Form.Label className="small fw-bold mb-1">Horário Substituto / Alternativo:</Form.Label>
                                            <Form.Select
                                                size="sm"
                                                className="input-mobile-nozoom"
                                                value={horarioSubst}
                                                onChange={e => setHorarioSubst(e.target.value)}
                                            >
                                                <option value="">Selecione para aplicar...</option>
                                                {horarios.map(h => (
                                                    <option key={h.codigo} value={h.codigo}>
                                                        {h.codigo} - {h.descricao}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Col>

                                        <Col xs={12} className="d-flex align-items-center justify-content-between flex-wrap gap-2 pt-2 border-top">
                                            <div className="d-flex align-items-center gap-2.5">
                                                <Form.Check
                                                    type="radio"
                                                    id="radioFolga"
                                                    label="Folga (F)"
                                                    name="modoEscala"
                                                    checked={modoMarcacao === 'F'}
                                                    onChange={() => setModoMarcacao('F')}
                                                    className="fw-bold text-success small"
                                                />
                                                <Form.Check
                                                    type="radio"
                                                    id="radioSubst"
                                                    label="Substituto (S)"
                                                    name="modoEscala"
                                                    checked={modoMarcacao === 'S'}
                                                    onChange={() => setModoMarcacao('S')}
                                                    className="fw-bold text-primary small"
                                                />
                                            </div>

                                            <div className="d-flex gap-1.5 ms-auto">
                                                <Button
                                                    size="sm"
                                                    variant="outline-danger"
                                                    onClick={handleLimparEscala}
                                                    disabled={salvando}
                                                >
                                                    <FaTrashCan />
                                                </Button>

                                                <Button
                                                    size="sm"
                                                    className="btn-arbor fw-bold"
                                                    onClick={handleSalvarEscala}
                                                    disabled={salvando}
                                                >
                                                    {salvando ? <Spinner animation="border" size="sm" /> : "Gravar"}
                                                </Button>
                                            </div>
                                        </Col>
                                    </Row>

                                    {/* GRID DO CALENDÁRIO INTERATIVO */}
                                    {loadingEscala ? (
                                        <div className="text-center py-5">
                                            <Spinner animation="border" className="text-danger" />
                                            <div className="mt-2 text-muted small">Carregando escala do colaborador...</div>
                                        </div>
                                    ) : (
                                        <div className="table-responsive p-0 m-0">
                                            <table className="table table-bordered grid-cadastro-escala text-center align-middle m-0">
                                                <thead className="table-secondary x-small text-uppercase">
                                                    <tr>
                                                        <th>Dom</th>
                                                        <th>Seg</th>
                                                        <th>Ter</th>
                                                        <th>Qua</th>
                                                        <th>Qui</th>
                                                        <th>Sex</th>
                                                        <th>Sáb</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {Array.from({ length: diasMesGrid.length / 7 }, (_, i) => (
                                                        <tr key={i}>
                                                            {diasMesGrid.slice(i * 7, i * 7 + 7).map((item, j) => {
                                                                if (item.vazio) return <td key={j} className="bg-light p-0"></td>;

                                                                const { dia, info } = item;
                                                                let celulaClasse = "celula-trabalho";
                                                                let badgeTexto = horarioPadrao || "";

                                                                if (info) {
                                                                    if (info.tipo === 'F') {
                                                                        celulaClasse = "celula-folga";
                                                                        badgeTexto = "FOLGA";
                                                                    } else if (info.tipo === 'S') {
                                                                        celulaClasse = "celula-subst";
                                                                        badgeTexto = info.horario || info.codigo;
                                                                    } else if (info.tipo === 'A') {
                                                                        celulaClasse = "celula-niver";
                                                                        badgeTexto = "🎂 NIVER";
                                                                    } else if (info.tipo === 'X') {
                                                                        celulaClasse = "celula-ferias";
                                                                        badgeTexto = "🏖️ FÉRIAS";
                                                                    }
                                                                }

                                                                return (
                                                                    <td
                                                                        key={j}
                                                                        className={`celula-dia-interativa ${celulaClasse}`}
                                                                        onClick={() => handleCellClick(item)}
                                                                    >
                                                                        <div className="num-dia-grid">{String(dia).padStart(2, '0')}</div>
                                                                        {badgeTexto && (
                                                                            <span className="badge-horario-grid">
                                                                                {badgeTexto}
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        ) : (
                            <Card className="border-0 shadow-sm text-center py-5">
                                <Card.Body className="text-muted">
                                    Selecione um funcionário da lista para cadastrar ou visualizar a escala.
                                </Card.Body>
                            </Card>
                        )}
                    </Col>
                </Row>
            </Container>

            <Footer />

            {/* MODAL DE AMPLIAÇÃO DA FOTO */}
            <Foto
                show={!!fotoAmpliada}
                onClose={() => setFotoAmpliada(null)}
                foto={fotoAmpliada?.foto}
                nome={fotoAmpliada?.nome}
                info01={fotoAmpliada?.info01}
                info02={fotoAmpliada?.info02}
            />
        </div>
    );
}

export default CadastroEscala;